
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Clock, ArrowRight, FileText, Gift, CheckCircle2, History, List, Wallet, Loader2, ExternalLink, Sparkles, HelpCircle, Box, Package, Truck, MapPin, X } from 'lucide-react';
import { fetchSurveys, fetchUserCompletedSurveyIds } from '../services/surveyService';
import { fetchUserRewards, claimRewardOnChain, openLootBox, submitShippingInfo } from '../services/rewardService';
import { Survey, RewardLog, LocalizedText } from '../types';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

interface SurveyViewProps {
  view: 'campaigns' | 'gifts';
}

const SurveyView: React.FC<SurveyViewProps> = ({ view }) => {
  // Sub-tabs for Campaigns View
  const [campaignTab, setCampaignTab] = useState<'latest' | 'completed'>('latest');
  
  // Sub-tabs for Gifts View
  const [giftTab, setGiftTab] = useState<'DIGITAL' | 'PHYSICAL'>('DIGITAL');

  const [allSurveys, setAllSurveys] = useState<Survey[]>([]);
  const [completedSurveyIds, setCompletedSurveyIds] = useState<Set<string>>(new Set());
  const [userRewards, setUserRewards] = useState<RewardLog[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Action States
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [shippingModalItem, setShippingModalItem] = useState<RewardLog | null>(null);
  const [shippingAddress, setShippingAddress] = useState('');
  
  const navigate = useNavigate();

  const loadData = async () => {
    try {
      const [surveys, completedIds, rewards] = await Promise.all([
        fetchSurveys(),
        fetchUserCompletedSurveyIds(),
        fetchUserRewards()
      ]);
      setAllSurveys(surveys);
      setCompletedSurveyIds(new Set(completedIds));
      setUserRewards(rewards);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [view]);

  // Use the revalidation hook to refresh data on focus
  useAutoRefresh(loadData);

  const handleStartSurvey = (id: string) => {
    navigate(`/survey/${id}`);
  };

  const getLocalizedText = (obj: LocalizedText) => obj.en || Object.values(obj)[0] || 'Untitled';

  // --- DIGITAL CLAIM HANDLER ---
  const handleClaimDigital = async (rewardId: string) => {
      if (!(window as any).ethereum) {
          alert("No crypto wallet found! Please install MetaMask or use a Web3 browser.");
          return;
      }

      setProcessingId(rewardId);
      try {
          const accounts = await (window as any).ethereum.request({ method: 'eth_requestAccounts' });
          const walletAddress = accounts[0];

          await claimRewardOnChain(rewardId, walletAddress);
          await loadData();
          
      } catch (e: any) {
          console.error(e);
          alert("Claim failed: " + e.message);
      } finally {
          setProcessingId(null);
      }
  };

  // --- PHYSICAL OPEN HANDLER ---
  const handleOpenLootBox = async (rewardLog: RewardLog) => {
      if (!rewardLog.loot_table_id) return;
      setProcessingId(rewardLog.id);
      
      try {
          // Artificial delay for suspense
          await new Promise(r => setTimeout(r, 1500));
          await openLootBox(rewardLog.id, rewardLog.loot_table_id);
          await loadData();
      } catch (e: any) {
          console.error(e);
          alert("Failed to open box: " + e.message);
      } finally {
          setProcessingId(null);
      }
  };

  const handleShippingSubmit = async () => {
      if (!shippingModalItem || !shippingAddress) return;
      setProcessingId('shipping');
      try {
          await submitShippingInfo(shippingModalItem.id, shippingAddress);
          setShippingModalItem(null);
          setShippingAddress('');
          await loadData();
          alert("Shipping requested successfully!");
      } catch (e) {
          alert("Failed to save address.");
      } finally {
          setProcessingId(null);
      }
  };

  const calculateTotalEarnings = () => {
      return userRewards
        .filter(r => (r.currency === 'USDT' || r.action_type === 'DIGITAL_REWARD') && r.status === 'CLAIMED')
        .reduce((sum, r) => sum + Number(r.amount || 0), 0)
        .toFixed(2);
  };
  
  const countInventory = () => {
      return userRewards.filter(r => r.action_type === 'LOOT_ITEM_WON' || r.action_type === 'LOOT_BOX').length;
  };

  const renderCampaignsView = () => {
      const latestCampaigns = allSurveys.filter(s => !completedSurveyIds.has(s.id) && s.status === 'ACTIVE');
      const completedSurveys = allSurveys.filter(s => completedSurveyIds.has(s.id));

      return (
          <div className="space-y-6">
              {/* Campaign Sub-Tabs */}
              <div className="flex bg-slate-800 p-1 rounded-lg w-full md:w-fit">
                  <button 
                    onClick={() => setCampaignTab('latest')}
                    className={`flex-1 md:flex-none px-6 py-2.5 rounded-md text-sm font-medium transition-all ${campaignTab === 'latest' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                  >
                      Latest Campaigns
                  </button>
                  <button 
                    onClick={() => setCampaignTab('completed')}
                    className={`flex-1 md:flex-none px-6 py-2.5 rounded-md text-sm font-medium transition-all ${campaignTab === 'completed' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                  >
                      Completed
                  </button>
              </div>

              {campaignTab === 'latest' ? (
                  <>
                    {latestCampaigns.length === 0 ? (
                        <div className="text-center py-20 bg-slate-800/50 rounded-2xl border border-slate-700 border-dashed">
                            <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FileText size={32} className="text-slate-600" />
                            </div>
                            <h3 className="text-xl font-medium text-slate-300">No active campaigns</h3>
                            <p className="text-slate-500 mt-2">You're all caught up! Check back later.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {latestCampaigns.map(survey => (
                                <div 
                                    key={survey.id} 
                                    className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-indigo-500/50 hover:shadow-xl hover:shadow-indigo-500/10 group transition-all flex flex-col h-full"
                                >
                                    <div className="flex justify-between items-start mb-4">
                                        <span className={`text-xs font-bold px-2 py-1 rounded uppercase tracking-wider flex items-center gap-1 ${
                                            survey.reward_type === 'PHYSICAL' ? 'bg-purple-500/10 text-purple-400' : 'bg-emerald-500/10 text-emerald-400'
                                        }`}>
                                            {survey.reward_type === 'PHYSICAL' ? <Box size={10} /> : <Sparkles size={10} />}
                                            {survey.reward_type === 'PHYSICAL' ? 'Loot Drop' : 'Token Pool'}
                                        </span>
                                        <span className="text-slate-500 text-xs flex items-center gap-1">
                                            <Clock size={12} /> ~5 min
                                        </span>
                                    </div>

                                    <h3 className="text-xl font-bold mb-2 line-clamp-2 text-white group-hover:text-indigo-400 transition-colors">
                                        {getLocalizedText(survey.title)}
                                    </h3>
                                    <p className="text-slate-400 text-sm mb-6 line-clamp-3 flex-1">
                                        {getLocalizedText(survey.description) || "Join this campaign to help verify local data."}
                                    </p>

                                    <button 
                                        onClick={() => handleStartSurvey(survey.id)}
                                        className="w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 bg-slate-700 hover:bg-indigo-600 text-white group-hover:shadow-lg"
                                    >
                                        Start Survey <ArrowRight size={18} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                  </>
              ) : (
                  <>
                    {completedSurveys.length === 0 ? (
                        <div className="text-center py-20 bg-slate-800/50 rounded-2xl border border-slate-700 border-dashed">
                            <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                <History size={32} className="text-slate-600" />
                            </div>
                            <h3 className="text-xl font-medium text-slate-300">No history yet</h3>
                            <p className="text-slate-500 mt-2">Complete surveys to see them listed here.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {completedSurveys.map(survey => (
                                <div key={survey.id} className="bg-slate-800 rounded-xl p-5 border border-slate-700 flex flex-col md:flex-row md:items-center gap-4 opacity-75 hover:opacity-100 transition-opacity">
                                    <div className="bg-emerald-900/20 p-3 rounded-lg border border-emerald-900/50 text-emerald-400 shrink-0">
                                        <CheckCircle2 size={24} />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-bold text-white text-lg">{getLocalizedText(survey.title)}</h4>
                                        <div className="flex items-center gap-4 text-xs text-slate-400 mt-1">
                                            <span>Completed</span>
                                            {survey.reward_type === 'PHYSICAL' ? (
                                                <span className="flex items-center gap-1 text-purple-400"><Box size={10} /> Loot Box Earned</span>
                                            ) : (
                                                <span className="flex items-center gap-1 text-emerald-400"><DollarSign size={10} /> Coupon Generated</span>
                                            )}
                                        </div>
                                    </div>
                                    <button 
                                        disabled 
                                        className="px-4 py-2 bg-slate-900 text-slate-500 rounded-lg text-sm font-medium border border-slate-700 cursor-default"
                                    >
                                        Submitted
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                  </>
              )}
          </div>
      );
  };

  const renderGiftsView = () => {
      const digitalItems = userRewards.filter(r => r.action_type === 'DIGITAL_REWARD');
      const physicalItems = userRewards.filter(r => r.action_type === 'LOOT_BOX' || r.action_type === 'LOOT_ITEM_WON');

      return (
        <div className="space-y-6">
             {/* Reward Type Toggle */}
             <div className="flex justify-center mb-8">
                 <div className="flex bg-slate-800 p-1 rounded-full border border-slate-700">
                     <button
                        onClick={() => setGiftTab('DIGITAL')}
                        className={`px-6 py-2 rounded-full text-sm font-bold flex items-center gap-2 transition-all ${giftTab === 'DIGITAL' ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
                     >
                         <Wallet size={16} /> Digital Wallet
                     </button>
                     <button
                        onClick={() => setGiftTab('PHYSICAL')}
                        className={`px-6 py-2 rounded-full text-sm font-bold flex items-center gap-2 transition-all ${giftTab === 'PHYSICAL' ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
                     >
                         <Package size={16} /> Inventory
                     </button>
                 </div>
             </div>

             {/* --- DIGITAL TAB --- */}
             {giftTab === 'DIGITAL' && (
                 <div className="grid gap-4">
                    {digitalItems.length === 0 && <div className="text-center text-slate-500 py-12">No digital rewards yet.</div>}
                    
                    {digitalItems.map(log => (
                        <div key={log.id} className={`bg-slate-800 p-5 rounded-xl border transition-all flex flex-col md:flex-row md:items-center justify-between gap-4 ${
                            log.status === 'AVAILABLE' 
                            ? 'border-emerald-500/50 shadow-lg shadow-emerald-900/10 bg-gradient-to-r from-slate-800 to-emerald-900/10' 
                            : 'border-slate-700 opacity-80 hover:opacity-100'
                        }`}>
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center border transition-transform ${
                                    log.status === 'AVAILABLE' ? 'bg-emerald-600 text-white border-emerald-400' : 'bg-slate-700 text-slate-400 border-slate-600'
                                }`}>
                                    {log.status === 'AVAILABLE' ? <HelpCircle size={24} className="animate-pulse" /> : <DollarSign size={24} />}
                                </div>
                                <div>
                                    <h4 className="font-bold text-white text-lg flex items-center gap-2">
                                        {log.status === 'AVAILABLE' ? 'Mystery Reward' : 'Revealed Token'}
                                        {log.status === 'AVAILABLE' && <span className="bg-emerald-600 text-white text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide">Action Required</span>}
                                    </h4>
                                    <p className="text-xs text-slate-400 font-mono">
                                        {log.status === 'AVAILABLE' ? 'Chain Verification Pending' : `Tx: ${log.tx_hash?.substring(0,8)}...`}
                                    </p>
                                </div>
                            </div>
                            
                            <div className="flex items-center justify-between md:justify-end gap-6 border-t md:border-t-0 border-slate-700 pt-4 md:pt-0">
                                <div className="text-right">
                                    <span className={`block text-xl font-bold ${log.status === 'AVAILABLE' ? 'text-emerald-200 blur-[2px]' : 'text-emerald-400'}`}>
                                        {log.status === 'AVAILABLE' ? '??.??' : `+${log.amount}`} {log.currency || 'USDT'}
                                    </span>
                                    {log.status === 'AVAILABLE' && <span className="text-[10px] text-emerald-300">Randomized on Chain</span>}
                                </div>
                                
                                {log.status === 'AVAILABLE' ? (
                                    <button 
                                        onClick={() => handleClaimDigital(log.id)}
                                        disabled={processingId === log.id}
                                        className="bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2.5 rounded-lg font-bold shadow-lg shadow-emerald-500/30 active:scale-95 transition-all flex items-center gap-2"
                                    >
                                        {processingId === log.id ? <Loader2 className="animate-spin" size={18}/> : <Wallet size={18} />}
                                        Claim
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-1 text-slate-500 bg-slate-900 px-3 py-1.5 rounded border border-slate-700">
                                        <CheckCircle2 size={14} /> Claimed
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                 </div>
             )}

             {/* --- PHYSICAL TAB --- */}
             {giftTab === 'PHYSICAL' && (
                 <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                     {physicalItems.length === 0 && <div className="col-span-full text-center text-slate-500 py-12">Inventory empty. Complete campaigns with Loot Boxes!</div>}
                     
                     {physicalItems.map(log => {
                         const isOpen = log.status !== 'AVAILABLE';
                         const isShipped = log.status === 'SHIPPED';

                         return (
                             <div key={log.id} className={`relative group rounded-xl p-4 border transition-all flex flex-col items-center text-center ${
                                 !isOpen 
                                 ? 'bg-slate-800 border-purple-500/50 hover:border-purple-400 hover:shadow-lg hover:shadow-purple-500/20' 
                                 : 'bg-slate-900 border-slate-700'
                             }`}>
                                 {/* Image / Icon */}
                                 <div className={`w-32 h-32 mb-4 flex items-center justify-center relative ${!isOpen ? 'animate-bounce-slow' : ''}`}>
                                     {!isOpen ? (
                                         <Box size={80} className="text-purple-400 drop-shadow-[0_0_15px_rgba(168,85,247,0.5)]" strokeWidth={1} />
                                     ) : (
                                         log.item_image ? (
                                             <img src={log.item_image} alt={log.item_name} className="w-full h-full object-contain drop-shadow-xl" />
                                         ) : (
                                             <Gift size={64} className="text-yellow-400" />
                                         )
                                     )}
                                 </div>

                                 {/* Details */}
                                 <h4 className={`font-bold text-lg mb-1 ${!isOpen ? 'text-purple-300' : 'text-white'}`}>
                                     {!isOpen ? 'Loot Box' : (log.item_name || 'Mystery Item')}
                                 </h4>
                                 
                                 <p className="text-xs text-slate-500 mb-4 h-4">
                                     {!isOpen ? 'Tap to Reveal' : (isShipped ? 'Shipping Requested' : 'In Inventory')}
                                 </p>

                                 {/* Action Button */}
                                 {!isOpen ? (
                                     <button 
                                        onClick={() => handleOpenLootBox(log)}
                                        disabled={processingId === log.id}
                                        className="w-full bg-purple-600 hover:bg-purple-500 text-white py-2 rounded-lg font-bold shadow-lg shadow-purple-900/50 active:scale-95 transition-all flex items-center justify-center gap-2"
                                     >
                                         {processingId === log.id ? <Loader2 className="animate-spin" size={16}/> : <Sparkles size={16} />}
                                         Open Box
                                     </button>
                                 ) : (
                                     <button 
                                        onClick={() => setShippingModalItem(log)}
                                        disabled={isShipped}
                                        className={`w-full py-2 rounded-lg font-medium text-sm flex items-center justify-center gap-2 border ${
                                            isShipped 
                                            ? 'bg-slate-800 text-slate-500 border-slate-700 cursor-default' 
                                            : 'bg-slate-800 text-slate-300 border-slate-600 hover:border-slate-400 hover:text-white'
                                        }`}
                                     >
                                         {isShipped ? <CheckCircle2 size={14}/> : <Truck size={14} />}
                                         {isShipped ? 'Processing' : 'Ship to Me'}
                                     </button>
                                 )}
                             </div>
                         );
                     })}
                 </div>
             )}
        </div>
      );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500 relative">
      
      {/* Header & Stats (Common for both views) */}
      <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-slate-700 pb-6 gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-white mb-2">
              {view === 'campaigns' ? 'Campaign Center' : 'My Gift Box'}
          </h1>
          <p className="text-slate-400">
              {view === 'campaigns' ? 'Participate in data campaigns to earn coupons.' : 'Manage your digital assets and physical loot.'}
          </p>
        </div>
        
        {/* Wallet / Stats Card */}
        <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700 flex gap-6 items-center shadow-lg backdrop-blur-sm">
            <div>
                <span className="block text-xs text-slate-400 uppercase font-bold tracking-wider mb-1">Crypto Value</span>
                <span className="block text-2xl font-bold text-emerald-400 font-mono flex items-center gap-1">
                    <DollarSign size={20}/> {calculateTotalEarnings()}
                </span>
            </div>
            <div className="h-8 w-px bg-slate-700"></div>
            <div>
                <span className="block text-xs text-slate-400 uppercase font-bold tracking-wider mb-1">Items</span>
                <span className="block text-2xl font-bold text-white font-mono flex items-center gap-1">
                    <Box size={20} className="text-purple-400"/> {countInventory()}
                </span>
            </div>
        </div>
      </div>

      {view === 'campaigns' && renderCampaignsView()}
      {view === 'gifts' && renderGiftsView()}
      
      {/* Shipping Modal */}
      {shippingModalItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
              <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-2xl max-w-md w-full p-6 relative">
                  <button 
                    onClick={() => setShippingModalItem(null)} 
                    className="absolute top-4 right-4 text-slate-500 hover:text-white"
                  >
                      <X size={20} />
                  </button>
                  
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <Truck className="text-purple-400" /> Request Shipping
                  </h3>
                  
                  <div className="flex gap-4 mb-6 bg-slate-900/50 p-4 rounded-lg border border-slate-700/50">
                      {shippingModalItem.item_image ? (
                          <img src={shippingModalItem.item_image} className="w-16 h-16 object-contain bg-slate-800 rounded" />
                      ) : (
                          <div className="w-16 h-16 bg-slate-800 rounded flex items-center justify-center"><Gift className="text-slate-500"/></div>
                      )}
                      <div>
                          <p className="text-slate-400 text-xs uppercase font-bold">Item</p>
                          <p className="text-white font-medium">{shippingModalItem.item_name}</p>
                      </div>
                  </div>

                  <div className="space-y-4">
                      <div>
                          <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Shipping Address / Contact Info</label>
                          <textarea 
                             value={shippingAddress}
                             onChange={e => setShippingAddress(e.target.value)}
                             placeholder="Full Name, Phone Number, Street Address..."
                             className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white h-24 focus:ring-2 focus:ring-purple-500 outline-none resize-none"
                          />
                      </div>
                      <button 
                        onClick={handleShippingSubmit}
                        disabled={!shippingAddress || processingId === 'shipping'}
                        className="w-full bg-purple-600 hover:bg-purple-500 text-white py-3 rounded-lg font-bold shadow-lg shadow-purple-900/20 active:scale-95 transition-all flex items-center justify-center gap-2"
                      >
                          {processingId === 'shipping' ? <Loader2 className="animate-spin" /> : <MapPin size={18} />}
                          Confirm Shipment
                      </button>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

export default SurveyView;
